import React from "react";
import {
  AnchorLink,
  Button,
  Chip,
  Container,
  Icon,
  IconList,
  Layout,
  Monitoring,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { usePeopleReportDispatch } from "../../../hooks/peopleReportHook";
import ProtectionShell from "./ProtectionShell";
import StatusPill from "./StatusPill";
import {
  dismissWelcome,
  fetchCustomerBrokersAsync,
  selectNeedsAction,
  selectOptOutType,
  selectProtectionProgress,
  selectProtectionState,
} from "../../../features/profileProtection/profileProtectionSlice";
import ProfileCard from "./ProfileCard";
import {
  checkCurrentlyMonitoring,
  getMonitoringAsync,
  selectMonitoring,
  toggleMonitoringAsync,
} from "../../../features/monitoring/monitoringSlice";
import { selectPeopleReport } from "../../../features/reports/reportsSlice";

const StatusHub: React.FC = () => {
  const dispatch = useAppDispatch();
  const pullPeopleReport = usePeopleReportDispatch();
  const state = useAppSelector(selectProtectionState);
  const progress = useAppSelector(selectProtectionProgress);
  const tasks = useAppSelector(selectNeedsAction);
  const optOutType = useAppSelector(selectOptOutType);
  const monitoredReports = useAppSelector(selectMonitoring);
  const pulledReport = useAppSelector(selectPeopleReport);
  const selectedMonitoredReport = React.useMemo(
    () =>
      monitoredReports.find(report => report.reportToken === state.reportToken),
    [monitoredReports, state.reportToken],
  );
  const currentlyMonitoringSelectedReport = useAppSelector(rootState => {
    return checkCurrentlyMonitoring(rootState, state.reportToken);
  });
  const siteStatuses = state.brokerSitesStatus;
  const scannedDateDisplay = React.useMemo(() => {
    if (!state.lastBrokerScanDate) {
      return new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }

    return new Date(`${state.lastBrokerScanDate}T00:00:00`).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      },
    );
  }, [state.lastBrokerScanDate]);

  const shouldRefreshBrokerScan = React.useMemo(() => {
    if (!state.lastBrokerScanDate) {
      return true;
    }

    const today = new Date();
    const lastScanDate = new Date(`${state.lastBrokerScanDate}T00:00:00`);
    today.setHours(0, 0, 0, 0);

    return lastScanDate < today;
  }, [state.lastBrokerScanDate]);
  const selectedReportVersion = React.useMemo(() => {
    if (selectedMonitoredReport?.version) {
      return selectedMonitoredReport.version;
    }

    if (pulledReport && pulledReport.reportToken === state.reportToken) {
      return pulledReport.reportSummary?.version;
    }

    return undefined;
  }, [pulledReport, selectedMonitoredReport, state.reportToken]);

  React.useEffect(() => {
    dispatch(getMonitoringAsync());
  }, [dispatch]);

  React.useEffect(() => {
    if (shouldRefreshBrokerScan) {
      dispatch(fetchCustomerBrokersAsync());
    }
  }, [dispatch, shouldRefreshBrokerScan]);

  const handleToggleMonitoring = () => {
    if (!state.reportToken) {
      return;
    }

    const todayVersion = new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, "");
    const versionToToggle = selectedReportVersion || todayVersion;

    dispatch(
      toggleMonitoringAsync({
        reportToken: state.reportToken,
        version: versionToToggle,
      }),
    ).then(action => {
      if (
        toggleMonitoringAsync.fulfilled.match(action) &&
        action.payload.toggleState
      ) {
        dispatch(getMonitoringAsync());
      }
    });
  };
  const profileCardStatusTextMap = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
  };
  const profileCardStatusColorMap = {
    not_started: "grey" as const,
    in_progress: "yellow" as const,
    completed: "green" as const,
  };
  const [openSiteIndices, setOpenSiteIndices] = React.useState<Set<number>>(
    new Set(),
  );
  const sortedSiteStatuses = React.useMemo(
    () =>
      siteStatuses
        ? [...siteStatuses].sort((a, b) => {
            const aDone = a.statusLabel?.toLowerCase() === "done" ? 1 : 0;
            const bDone = b.statusLabel?.toLowerCase() === "done" ? 1 : 0;
            return aDone - bDone;
          })
        : [],
    [siteStatuses],
  );
  const inProgressCount = sortedSiteStatuses.filter(
    s => s.statusLabel?.toLowerCase() !== "done",
  ).length;
  const doneCount = sortedSiteStatuses.filter(
    s => s.statusLabel?.toLowerCase() === "done",
  ).length;

  const savedProfileName = state.profileName || "Protected Profile";
  const savedProfileLocation = state.profileLocation || null;

  const handleViewProfile = () => {
    if (!state.reportToken) {
      return;
    }

    pullPeopleReport(state.reportToken, "profile protection status");
  };

  return (
    <ProtectionShell title="Protection status" subtitle="">
      {!state.welcomeDismissed ? (
        <Container
          altColor="selected"
          border
          borderColor="primary"
          bodyBorderRadius
        >
          <Padding space="md">
            <Layout
              type="flex"
              flexProps={{
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "sm",
              }}
            >
              <Layout
                type="flex"
                flexProps={{
                  gap: "sm",
                  alignItems: "flex-start",
                  direction: "row",
                }}
              >
                <Icon
                  name={IconList.IconIdProtectCircleCheck}
                  size={30}
                  iconColor="var(--sc-text-color-primary)"
                />
                <Layout
                  type="flex"
                  flexProps={{ direction: "column", gap: "xxxs" }}
                >
                  <Text fontWeight="bold">Removals started</Text>
                  <Text size="sm">
                    We&apos;ve submitted opt-out requests to{" "}
                    {siteStatuses.length} sources on your behalf. Some sources
                    may email you a confirmation link. Check your inbox and
                    follow any instructions to complete removal.
                  </Text>
                </Layout>
              </Layout>
              <AnchorLink
                role="button"
                handleClick={() => dispatch(dismissWelcome())}
              >
                Dismiss
              </AnchorLink>
            </Layout>
          </Padding>
        </Container>
      ) : null}

      <ProfileCard
        name={savedProfileName}
        location={savedProfileLocation}
        showStatus={true}
        statusText={
          state.status ? profileCardStatusTextMap[state.status] : "Unknown"
        }
        statusColor={
          state.status ? profileCardStatusColorMap[state.status] : "grey"
        }
      />

      <Container altColor="selected" border bodyBorderRadius>
        <Padding space="md">
          <Layout
            type="flex"
            flexProps={{
              gap: "sm",
              alignItems: "flex-start",
              direction: "row",
            }}
          >
            <Icon name={IconList.IconHelp} size={20} />
            <Layout
              type="flex"
              flexProps={{ direction: "column", gap: "xxxs" }}
            >
              <Text size="sm">
                This scan focused on high-risk people search sources, the most
                common sources of exposed personal information.
              </Text>
              <Text size="sm">
                We&apos;ll re-scan periodically, since data can reappear on some
                sources over time.
              </Text>
            </Layout>
          </Layout>
        </Padding>
      </Container>

      <Layout type="flex" flexProps={{ direction: "column" }}>
        <Container
          border
          borderThickness={1}
          bodyBorderRadius
          noBottomBorder={optOutType === "external"}
        >
          <Padding space="md">
            <Layout type="flex" flexProps={{ direction: "column", gap: "xs" }}>
              <Layout
                type="flex"
                flexProps={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text fontWeight="bold">Opt-out requests</Text>
              </Layout>
              <Layout
                type="flex"
                flexProps={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text size="sm">
                  {progress.removed} of {progress.total} sources removed
                </Text>
                <Text size="sm" color="success">
                  {progress.percent}%
                </Text>
              </Layout>
              <div className="profile-protection__progress-track">
                <div
                  className="profile-protection__progress-fill"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <Text size="xs" color="dark-gray">
                Scanned {scannedDateDisplay}
              </Text>
            </Layout>
          </Padding>
        </Container>
        {siteStatuses
          ? sortedSiteStatuses.map((site, index) => {
              const isDone = site.statusLabel?.toLowerCase() === "done";
              const isOpen = openSiteIndices.has(index);
              const steps = [
                { label: "Exposure detected", done: true },
                { label: "Opt-out request sent", done: true },
                { label: "Awaiting site confirmation", done: isDone },
                { label: "Record removed", done: isDone },
              ];
              return (
                <React.Fragment key={`${site.brandName}-${index}`}>
                  {index === 0 && inProgressCount > 0 && (
                    <Padding top="xs" bottom="xxs">
                      <Text size="sm" color="primary" fontWeight="semibold">
                        In progress ({inProgressCount})
                      </Text>
                    </Padding>
                  )}
                  {index === inProgressCount && doneCount > 0 && (
                    <Padding top="xs" bottom="xxs">
                      <Text size="sm" color="success" fontWeight="semibold">
                        Removed ({doneCount})
                      </Text>
                    </Padding>
                  )}
                  <div className="profile-protection__site-row">
                    <Container
                      border
                      borderThickness={1}
                      noTopBorder={index !== 0 && index !== inProgressCount}
                    >
                      <Padding space="md">
                        <Layout
                          type="flex"
                          flexProps={{ direction: "column", gap: "xs" }}
                        >
                          <div
                            onClick={() => {
                              const next = new Set(openSiteIndices);
                              isOpen ? next.delete(index) : next.add(index);
                              setOpenSiteIndices(next);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <Layout
                              type="flex"
                              flexProps={{
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Layout
                                type="flex"
                                flexProps={{ gap: "xs", alignItems: "center" }}
                              >
                                <div
                                  style={{
                                    transform: isOpen
                                      ? "rotate(0deg)"
                                      : "rotate(180deg)",
                                    transition: "transform 0.2s",
                                    display: "flex",
                                  }}
                                >
                                  <Icon name={IconList.IconCaret} size={12} />
                                </div>
                                <Text>{site.brandName}</Text>
                              </Layout>
                              <Chip
                                chipText={isDone ? "Removed" : "In progress"}
                                color={isDone ? "success" : "gray"}
                              />
                            </Layout>
                          </div>
                          {isOpen && (
                            <Padding left="md">
                              <Layout
                                type="flex"
                                flexProps={{
                                  direction: "column",
                                  gap: "xxs",
                                }}
                              >
                                {steps.map(step => (
                                  <Layout
                                    key={step.label}
                                    type="flex"
                                    flexProps={{
                                      gap: "xs",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Text
                                      color={
                                        step.done ? "success" : "dark-gray"
                                      }
                                      tag="span"
                                    >
                                      <Icon
                                        name={
                                          step.done
                                            ? IconList.IconCircleCheck
                                            : IconList.IconCircle
                                        }
                                        size={14}
                                      />
                                    </Text>
                                    <Text
                                      size="sm"
                                      color={step.done ? "base" : "dark-gray"}
                                    >
                                      {step.label}
                                    </Text>
                                  </Layout>
                                ))}
                                <Padding left="lg">
                                  <Text size="xs" color="dark-gray">
                                    Last updated: {scannedDateDisplay}
                                  </Text>
                                </Padding>
                              </Layout>
                            </Padding>
                          )}
                        </Layout>
                      </Padding>
                    </Container>
                  </div>
                </React.Fragment>
              );
            })
          : null}
      </Layout>

      <Container border bodyBorderRadius>
        <Padding space="md">
          <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
            <Layout
              type="flex"
              flexProps={{ gap: "xs", alignItems: "center", direction: "row" }}
            >
              <Icon name={IconList.IconShield} size={20} />

              <Text fontWeight="bold">Your Public Data Check profile</Text>
            </Layout>
            <Layout
              type="flex"
              flexProps={{ direction: "column", gap: "xxxs" }}
            >
              <Layout
                type="flex"
                flexProps={{
                  gap: "xs",
                  alignItems: "center",
                  direction: "row",
                }}
              >
                <Text color="primary">
                  <Icon name={IconList.IconEye} size={14} />
                </Text>

                <Text size="sm">You keep access to your record</Text>
              </Layout>
              <Layout
                type="flex"
                flexProps={{
                  gap: "xs",
                  alignItems: "center",
                  direction: "row",
                }}
              >
                <Text color="primary">
                  <Icon name={IconList.IconMonitoring} size={14} />
                </Text>
                <Text size="sm">Source data monitoring</Text>
              </Layout>
              <Layout
                type="flex"
                flexProps={{
                  gap: "xs",
                  alignItems: "center",
                  direction: "row",
                }}
              >
                <Text color="primary">
                  <Icon name={IconList.IconShield} size={14} />
                </Text>
                <Text size="sm">Hidden from others</Text>
              </Layout>
            </Layout>

            <Button
              buttonText="View your profile"
              color="primary"
              fill="fill"
              handleClick={handleViewProfile}
              disabled={!state.reportToken}
            />

            <hr
              style={{
                borderTop: "1px solid rgb(201, 203, 212)",
                height: "1px",
                width: "100%",
                borderRight: "none",
                borderLeft: "none",
                borderBottom: "none",
                margin: 0,
              }}
            />

            <Layout
              type="flex"
              flexProps={{
                gap: "xs",
                alignItems: "center",
                direction: "row",
                justifyContent: "space-between",
              }}
            >
              <Text size="sm">Alert me when my record changes</Text>
              <Monitoring
                handleMonitoring={handleToggleMonitoring}
                monitoringInput={""}
                initialState={currentlyMonitoringSelectedReport}
                disabled={!state.reportToken}
              />
            </Layout>

            <Layout type="flex" flexProps={{ justifyContent: "space-between" }}>
              <Text size="xs" color="dark-gray">
                Included with your plan at no extra cost.
              </Text>
              <Text size="xs" color="dark-gray">
                Change visibility setting
              </Text>
            </Layout>
          </Layout>
        </Padding>
      </Container>

      <Padding top="md">
        <Layout type="flex" flexProps={{ justifyContent: "center" }}>
          <AnchorLink role="link" href="/" target="_self">
            Back to dashboard
          </AnchorLink>
        </Layout>
      </Padding>
    </ProtectionShell>
  );
};

export default StatusHub;
